import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import debounce from 'lodash/debounce';
import { InputProps } from '../types';
import { searchIcon, cross } from 'assets/icons';
import { BaseInput } from '../base-input';

type SearchInputProps = InputProps & {
  handleSearch: (data: string) => void | Promise<any>;
  handleClear?: () => void;
  validatorFunction?: (value: string) => string;
  oneLetterSearch?: boolean;
  debounceSearchDelay?: number;
};

/* handleSearch - функция поиска, которая также может быть промисом.
 * handleClear - функция очистки value инпута.
 * validatorFunction - функция, валидирующая value инпута. Например, validateMailInputValue по пути utils/validate-utils.ts.
 * oneLetterSearch - булиновое значение, определяющее будет ли осуществляться поиск при вводе каждого символа.
 * По умолчанию только по нажатию Enter. Поиск по каждому символу обернут в debounce для оптимизации запросов поиска.
 * Также принимает в себя все пропсы BaseInput, подробно описаны в BaseInput. */
export const SearchInput: FC<SearchInputProps> = observer(
  ({
    handleSearch,
    handleClear,
    validatorFunction,
    oneLetterSearch,
    debounceSearchDelay = 250,
    ...inputProps
  }) => {
    const [pending, setPending] = useState<boolean>(false);
    const isMounted = useRef<boolean>(false);
    const lastSearchedValue = useRef<string>(
      (inputProps.value?.toString() || '').trim()
    );

    useEffect(() => {
      isMounted.current = true;

      return () => {
        isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (oneLetterSearch) {
        debouncedSearch(inputProps.value?.toString() || '');
      }

      return () => debouncedSearch.cancel();
    }, [inputProps.value, oneLetterSearch]);

    const isPromise = (value: any): value is Promise<any> => {
      return (
        !!value &&
        (typeof value === 'object' || typeof value === 'function') &&
        typeof value.then === 'function'
      );
    };

    const updatedHandleSearch = async (data: string) => {
      lastSearchedValue.current = data;

      const result = handleSearch(data);

      if (isPromise(result)) {
        setPending(true);
        await result;
        isMounted.current && setPending(false);
      }
    };

    const debouncedSearch = debounce((data: string) => {
      updatedHandleSearch(data);
    }, debounceSearchDelay);

    const upgradedOnKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
      const stringValue = (inputProps.value?.toString() || '').trim();

      if ([e.code, e.key].includes('Enter')) {
        if (stringValue !== '' || lastSearchedValue.current !== '') {
          updatedHandleSearch(stringValue);
        }
      }

      inputProps.onKeyDown && inputProps.onKeyDown(e);
    };

    const upgradedOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
      e.currentTarget.value = !!validatorFunction
        ? validatorFunction(e.target.value)
        : e.target.value;

      inputProps?.onChange && inputProps.onChange(e);
    };

    const handleClearField = (): void => {
      lastSearchedValue.current = '';

      handleClear && handleClear();

      inputProps.onChange?.({
        target: { value: '' },
      } as ChangeEvent<HTMLInputElement>);
    };

    return (
      <BaseInput
        icon={searchIcon}
        actionIcon={
          !!inputProps.value
            ? { src: cross, action: handleClearField, pending }
            : undefined
        }
        {...inputProps}
        onChange={upgradedOnChange}
        onKeyDown={upgradedOnKeyDown}
      />
    );
  }
);
